// THE QUICK TOUR's layer (functions/onboardingTour.js has the stops and the
// state). Mounted by app.js only while `root._onboardingTour` is set.
//
// A modal layer in place (`inertOutside`): the page behind is inert and dims,
// a window over the current stop's element lets it show through, and a card
// beside it says what it is — counter, name, one line, then Skip tour / Back /
// Next (Done on the closing card). Escape and Close end the tour; the arrow
// keys walk it. The card is a named dialog (role, aria-modal, labelled by its
// name, described by its line) and keeps the focus on its Next button.
//
// GEOMETRY. The layer publishes the stop's box as custom properties
// (onboardingTourVars) and every position is CSS over them: four sides of the
// house veil (`backdrop`) round the box plus a gap, a ring on the gap; the
// card sits below a target in the upper half of the viewport and above one in
// the lower half (`data-side`), its left edge clamped to the viewport so it
// never spills. Nothing is measured but the target. THE GAP is `Y`, or the
// box's room to the nearest screen edge when that is less (`--tour-gap`): the
// header tabs sit 5px under the top edge, and a `Y` ring there lost most of
// its top border off screen.
export const OnboardingTour = {
  position: 'fixed',
  inset: '0',
  zIndex: '2100',
  inertOutside: true,
  styleDeps: [(el, s) => s.root && s.root._onboardingTour],
  style: (el, s) => {
    const tour = s.root && s.root._onboardingTour
    return {
      ...(el.call('onboardingTourVars', tour ? tour.rect : null) || {}),
      '--tour-gap': 'min(var(--spacing-Y), var(--tour-room, var(--spacing-Y)))'
    }
  },
  onWindowResize: (e, el) => el.call('onboardingTourMeasure'),
  onDocumentScroll: {
    capture: true,
    passive: true,
    handler: (e, el) => el.call('onboardingTourMeasure')
  },
  onDocumentKeydown: (e, el) => {
    if (e.defaultPrevented) return
    if (e.key === 'Escape') {
      e.preventDefault()
      el.call('onboardingTourEnd')
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      el.call('onboardingTourGo', 1)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      el.call('onboardingTourGo', -1)
    }
  },

  // The closing card has no stop: the whole page takes the veil behind it.
  OnboardingTourVeil: {
    position: 'absolute',
    inset: '0',
    theme: 'backdrop',
    hideDeps: [(el, s) => s.root && s.root._onboardingTour],
    hide: (el, s) => {
      const tour = s.root && s.root._onboardingTour
      return !!(tour && tour.rect)
    }
  },

  // A stop: the house veil (the `backdrop` theme — scrim and blur, the one
  // behind every dialog) on the four sides of the stop, so the stop itself
  // shows through sharp and undimmed. The gap around it is the window's.
  OnboardingTourShade_top: {
    top: '0',
    left: '0',
    right: '0',
    height: 'calc(var(--tour-y) - var(--tour-gap))'
  },
  OnboardingTourShade_bottom: {
    top: 'calc(var(--tour-y) + var(--tour-h) + var(--tour-gap))',
    left: '0',
    right: '0',
    bottom: '0'
  },
  OnboardingTourShade_left: {
    top: 'calc(var(--tour-y) - var(--tour-gap))',
    left: '0',
    width: 'calc(var(--tour-x) - var(--tour-gap))',
    height: 'calc(var(--tour-h) + 2 * var(--tour-gap))'
  },
  OnboardingTourShade_right: {
    top: 'calc(var(--tour-y) - var(--tour-gap))',
    left: 'calc(var(--tour-x) + var(--tour-w) + var(--tour-gap))',
    right: '0',
    height: 'calc(var(--tour-h) + 2 * var(--tour-gap))'
  },

  // The window's ring — the one outline of the layer, in the ink that marks a
  // focused control, drawn round the stop.
  OnboardingTourRing: {
    position: 'absolute',
    boxSizing: 'border-box',
    round: 'radiusCard',
    borderWidth: 'hairline2',
    borderStyle: 'solid',
    borderColor: 'highlight',
    pointerEvents: 'none',
    ariaHidden: 'true',
    hideDeps: [(el, s) => s.root && s.root._onboardingTour],
    hide: (el, s) => {
      const tour = s.root && s.root._onboardingTour
      return !(tour && tour.rect)
    },
    top: 'calc(var(--tour-y) - var(--tour-gap))',
    left: 'calc(var(--tour-x) - var(--tour-gap))',
    width: 'calc(var(--tour-w) + 2 * var(--tour-gap))',
    height: 'calc(var(--tour-h) + 2 * var(--tour-gap))'
  },

  OnboardingTourCard: {}
}

// One side of the veil around a stop (its box is the call site's).
export const OnboardingTourShade = {
  position: 'absolute',
  theme: 'backdrop',
  ariaHidden: 'true',
  hideDeps: [(el, s) => s.root && s.root._onboardingTour],
  hide: (el, s) => {
    const tour = s.root && s.root._onboardingTour
    return !(tour && tour.rect)
  }
}

export const OnboardingTourCard = {
  role: 'dialog',
  ariaModal: 'true',
  ariaLabelledby: 'onboardingTourTitle',
  ariaDescribedby: 'onboardingTourText',
  tabindex: '-1',
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  boxSizing: 'border-box',
  padding: 'A',
  theme: 'dialog',
  color: 'paragraph',
  round: 'radiusSheet',
  shadow: 'overlay',
  animation: 'fadeInUpShort',
  animationDuration: 'C',
  animationFillMode: 'backwards',
  dataSide: (el, s) => {
    const tour = s.root && s.root._onboardingTour
    return el.call('onboardingTourSide', tour ? tour.rect : null)
  },
  // The card's width as a property, so its left edge can clamp to the
  // viewport in CSS: a spacing-token wide, never past the screen.
  style: {
    '--tour-card-w': 'min(var(--spacing-G1), calc(100vw - 2 * var(--spacing-A)))'
  },
  width: 'var(--tour-card-w)',
  maxHeight: 'calc(100dvh - 2 * var(--spacing-A))',
  overflowY: 'auto',
  '&[data-side="below"]': {
    top: 'min(calc(var(--tour-bottom) + var(--spacing-A)), calc(100dvh - var(--spacing-A)))',
    left: 'clamp(var(--spacing-A), calc(var(--tour-cx) - var(--tour-card-w) / 2), calc(100vw - var(--tour-card-w) - var(--spacing-A)))'
  },
  '&[data-side="above"]': {
    bottom: 'calc(100dvh - var(--tour-y) + var(--spacing-A))',
    left: 'clamp(var(--spacing-A), calc(var(--tour-cx) - var(--tour-card-w) / 2), calc(100vw - var(--tour-card-w) - var(--spacing-A)))'
  },
  // Centred by insets, never by a transform: the entrance animation owns
  // `transform`.
  '&[data-side="center"]': {
    inset: '0',
    margin: 'auto',
    height: 'fit-content'
  },
  // Tab cycles the card's own controls (the kit's trap; the page behind is
  // inert as well).
  onKeydown: (e, el) => el.call('appDialogTrapTab', e),

  TourHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'Z',
    minWidth: '0',
    AppMicroLabel: {
      text: (el, s) => {
        const cur = el.call('onboardingTourCurrent', s.root)
        if (!cur || cur.last) return el.call('polyglot', 'workspace.onboarding.tour.endEyebrow')
        return String(el.call('polyglot', 'workspace.onboarding.tour.counter') || '')
          .replace('{n}', String(cur.index + 1))
          .replace('{total}', String(cur.total))
      }
    },
    CloseButton: {
      ariaLabel: (el) => el.call('polyglot', 'workspace.onboarding.tour.close'),
      title: (el) => el.call('polyglot', 'workspace.onboarding.tour.close'),
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('onboardingTourEnd')
      }
    }
  },

  TourTitle: {
    tag: 'h2',
    id: 'onboardingTourTitle',
    margin: '0',
    fontSize: 'A',
    fontWeight: '700',
    lineHeight: '1.3',
    color: 'title',
    text: (el, s) => {
      const cur = el.call('onboardingTourCurrent', s.root)
      const key = cur && cur.stop ? cur.stop.title : 'workspace.onboarding.tour.endTitle'
      return el.call('polyglot', key)
    }
  },

  TourText: {
    tag: 'p',
    id: 'onboardingTourText',
    margin: '0',
    fontSize: 'Z2',
    lineHeight: '1.5',
    color: 'paragraph',
    text: (el, s) => {
      const cur = el.call('onboardingTourCurrent', s.root)
      const key = cur && cur.stop ? cur.text : 'workspace.onboarding.tour.endText'
      return el.call('onboardingText', key)
    }
  },

  TourFoot: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'Y',
    marginTop: 'Y',
    minWidth: '0',
    AppCapsuleBtnGhost: {
      compact: true,
      // The closing card has nothing left to skip: the box stays (Rule 66).
      visibility: (el, s) => {
        const cur = el.call('onboardingTourCurrent', s.root)
        return cur && cur.last ? 'hidden' : 'visible'
      },
      text: '{{ workspace.onboarding.tour.skip | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('onboardingTourEnd')
      }
    },
    TourSteps: {
      display: 'flex',
      alignItems: 'center',
      gap: 'Y',
      marginLeft: 'auto',
      AppCapsuleBtnGhost_back: {
        compact: true,
        visibility: (el, s) => {
          const cur = el.call('onboardingTourCurrent', s.root)
          return cur && cur.index > 0 ? 'visible' : 'hidden'
        },
        text: '{{ common.action.back | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('onboardingTourGo', -1)
        }
      },
      AppCapsuleBtnPrimary: {
        compact: true,
        // The layer moves the focus in on open — here, so Enter walks on.
        autofocus: true,
        text: (el, s) => {
          const cur = el.call('onboardingTourCurrent', s.root)
          return el.call(
            'polyglot',
            cur && cur.last ? 'workspace.onboarding.tour.done' : 'common.action.next'
          )
        },
        onClick: (e, el, s) => {
          e.stopPropagation()
          const cur = el.call('onboardingTourCurrent', s.root)
          if (!cur || cur.last) el.call('onboardingTourEnd')
          else el.call('onboardingTourGo', 1)
        }
      }
    }
  }
}
